"use client";

import PageHero from "@/components/PageHero";
import { ChevronDown } from "lucide-react";
import { useState } from "react";

interface FAQItem {
	id: number;
	question: string;
	answer: string;
}

const faqs: FAQItem[] = [
	{
		id: 1,
		question:
			"What age group does Reliance Future Football Academy (RFFA) admit?",
		answer:
			"RFFA admits talented boys between the ages of 11 and 15 who demonstrate both strong academic performance and football potential.",
	},
	{
		id: 2,
		question: "How can I apply to join the academy?",
		answer:
			"You can apply by filling out the online registration form on our website at www.rffacademy.org. Be sure to upload your academic results, a recent passport photo, and complete all required sections before submitting your application.",
	},
	{
		id: 3,
		question: "Does RFFA focus only on football training?",
		answer:
			"No. At RFFA, we combine academic excellence with professional football coaching. Our goal is to develop well-rounded student-athletes who excel both on the pitch and in the classroom.",
	},
	{
		id: 4,
		question: "Are there opportunities for players to study or play abroad?",
		answer:
			"Yes. Outstanding players at RFFA may have the opportunity to secure admission into reputable middle and high schools in the United States, where they can continue both their education and football development.",
	},
	{
		id: 5,
		question: "How can parents or guardians get involved with the academy?",
		answer:
			"RFFA values parental engagement as a key part of our philosophy. Parents are encouraged to stay involved in their child's growth, attend academy events, and communicate regularly with coaches and administrators.",
	},
];

export default function FAQPage() {
	const [expandedId, setExpandedId] = useState<number | null>(null);

	const toggleFAQ = (id: number) => {
		setExpandedId(expandedId === id ? null : id);
	};

	return (
		<div>
			<PageHero title="FAQs" image="/assets/black/1.jpg" alt="FAQ" />

			<section className="py-16 md:py-24 bg-gray-50">
				<div className="container mx-auto px-4 max-w-4xl">
					{/* Header */}
					<div className="text-center mb-16">
						<h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
							Frequently Asked Questions
						</h1>
						<p className="text-lg text-gray-600">
							Find answers to common questions about RFFA and our programs
						</p>
					</div>

					{/* FAQ Items */}
					<div className="space-y-4">
						{faqs.map((faq) => (
							<div
								key={faq.id}
								className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
							>
								<button
									onClick={() => toggleFAQ(faq.id)}
									className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
								>
									<h3 className="text-lg font-semibold text-gray-900 flex-1">
										{faq.question}
									</h3>
									<ChevronDown
										className={`w-5 h-5 text-primary flex-shrink-0 ml-4 transition-transform duration-300 ${
											expandedId === faq.id ? "rotate-180" : ""
										}`}
									/>
								</button>

								{expandedId === faq.id && (
									<div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
										<p className="text-gray-700 leading-relaxed">
											{faq.answer}
										</p>
									</div>
								)}
							</div>
						))}
					</div>

					{/* Contact Section */}
					<div className="mt-16 bg-primary/10 rounded-lg p-8 text-center border border-primary/20">
						<h2 className="text-2xl font-bold text-gray-900 mb-3">
							Didn&apos;t find your answer?
						</h2>
						<p className="text-gray-700 mb-6">
							If you have additional questions, please don&apos;t hesitate to
							contact us
						</p>
						<div className="space-y-3">
							<p className="flex items-center justify-center gap-2 text-gray-700">
								<span>📧</span>
								<a
									href="mailto:info@rffacademy.org"
									className="text-primary hover:underline font-semibold"
								>
									info@rffacademy.org
								</a>
							</p>
							<p className="flex items-center justify-center gap-2 text-gray-700">
								<span>📞</span>
								<a
									href="tel:+2349150154131"
									className="text-primary hover:underline font-semibold"
								>
									+234 915 015 4131
								</a>
							</p>
							<p className="flex items-center justify-center gap-2 text-gray-700">
								<span>📍</span>
								<span className="font-semibold">
									Secretary RFFA: B30, Grand Mall, Opposite Arisekola Alubarika
									Shopping Complex, Bodija, Ibadan, Oyo State.
								</span>
							</p>
						</div>
					</div>
				</div>
			</section>
		</div>
	);
}
